export const DEFAULT_HOUSEHOLD_FILTERS = Object.freeze({
  query: "", status: "all", reminder: "all", pickup: "all", includes: "all", sort: "newest",
});

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const text = value => typeof value === "string" || typeof value === "number" ? String(value) : "";
const normalize = value => text(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[‘’]/g, "'").toLocaleLowerCase().trim();

// Search aliases only; these must never be used to identify records for writes.
function printIdAlias(value) {
  const match = text(value).trim().match(/^(HH|HM|H|M)(?:-\d{4}-)?(\d+)$/i);
  return match ? `${match[1].toUpperCase().endsWith("M") ? "m" : "h"}${match[2].replace(/^0+/, "") || "0"}` : "";
}

export function householdAddressText(value) {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  return ["street", "street_address", "address1", "address2", "address_line_1", "address_line_2",
    "unit", "city", "state", "zip", "zip_code", "formatted_address"].map(key => text(value[key])).join(" ");
}

function searchableText(household, hidden) {
  const members = Array.isArray(household.children) ? household.children : [];
  const ids = [household.anonymous_id,
    ...members.flatMap(member => [member.anonymous_child_id, member.member_id, member.anonymous_id])];
  const parts = ids.flatMap(id => [text(id), printIdAlias(id), normalize(id).replace(/[^a-z0-9]/g, "")]);
  if (!hidden.has("names")) parts.push(household.client_name, ...members.map(member => member.name));
  if (!hidden.has("phone")) parts.push(household.client_phone, text(household.client_phone).replace(/\D/g, ""));
  if (!hidden.has("email")) parts.push(household.client_email);
  if (!hidden.has("address")) parts.push(householdAddressText(household.client_address));
  // Never index notes, family information or private legacy toy narratives.
  return normalize(parts.map(text).join(" "));
}

function searchTerms(query) {
  return normalize(query).split(/\s+/).filter(Boolean).map(term =>
    printIdAlias(term) || (/\d/.test(term) && /^[+()\d.-]+$/.test(term) ? term.replace(/\D/g, "") : term));
}

export function activeHouseholdFilterCount(filters = {}) {
  return ["status", "reminder", "pickup", "includes"].filter(key =>
    filters[key] && filters[key] !== DEFAULT_HOUSEHOLD_FILTERS[key]).length;
}

export function filterEnrolledHouseholds(households, values = {}, hiddenFields = []) {
  const filters = { ...DEFAULT_HOUSEHOLD_FILTERS, ...values };
  const hidden = new Set(Array.isArray(hiddenFields) ? hiddenFields : ["names", "phone", "email", "address"]);
  const terms = searchTerms(filters.query);
  const matches = (Array.isArray(households) ? households : []).map((household, index) => ({ household, index }))
    .filter(({ household: h }) => {
      if (filters.status === "active" && h.deleted_at) return false;
      if (filters.status === "removed" && !h.deleted_at) return false;
      if (filters.reminder === "sent" && !h.reminder_sent_at) return false;
      if (filters.reminder === "not_sent" && h.reminder_sent_at) return false;
      if (filters.reminder === "no_email" && !hidden.has("email") && text(h.client_email).trim()) return false;
      const pickup = text(h.pickup_window).trim();
      if (filters.pickup === "assigned" && !pickup) return false;
      if (filters.pickup === "unassigned" && pickup) return false;
      if (filters.pickup.startsWith("slot:") && pickup !== filters.pickup.slice(5)) return false;
      const ages = (Array.isArray(h.children) ? h.children : []).map(member => member.age)
        .filter(age => typeof age === "number" && Number.isFinite(age) && age >= 0);
      if (filters.includes === "toys" && !ages.some(age => age <= 9)) return false;
      if (filters.includes === "gift_cards" && !ages.some(age => age >= 10 && age <= 18)) return false;
      if (filters.includes === "seniors" && !ages.some(age => age >= 65)) return false;
      if (filters.includes === "duplicates" && (!h.duplicate_flag || h.deleted_at)) return false;
      if (terms.length) {
        const haystack = searchableText(h, hidden);
        const words = new Set(haystack.split(/\s+/));
        if (!terms.every(term => /^[hm]\d+$/.test(term) ? words.has(term) : haystack.includes(term))) return false;
      }
      return true;
    });
  const date = h => Date.parse(h.created_at || "") || 0;
  matches.sort((a, b) => {
    let order = 0;
    if (filters.sort === "name" && !hidden.has("names")) {
      order = collator.compare(text(a.household.client_name), text(b.household.client_name));
    } else if (filters.sort === "id" || filters.sort === "name") {
      order = collator.compare(printIdAlias(a.household.anonymous_id) || text(a.household.anonymous_id),
        printIdAlias(b.household.anonymous_id) || text(b.household.anonymous_id));
    } else {
      order = (date(a.household) - date(b.household)) * (filters.sort === "oldest" ? 1 : -1);
    }
    return order || a.index - b.index;
  });
  return matches.map(item => item.household);
}
